/**
 * The words and marks of a report's kind, status and section — shared by «My reports» and the
 * triage screen, so the two say the same thing in the same way (bata-platform#686, ADR 9063).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { StatusMarkKind } from '../../primitives';

import { REPORT_KINDS, REPORT_STATUSES, type ActorSide, type FeedbackKind, type FeedbackStatus } from './api';
import { GENERAL_SECTION, NEW_AREA_SECTION, feedbackSections } from './sections';

/** The mark each status takes — the word is the carrier, the symbol repeats it (§12.6). */
export const STATUS_MARK: Readonly<Record<FeedbackStatus, StatusMarkKind>> = {
  received: 'open',
  needs_info: 'modified',
  ready: 'open',
  approved_for_development: 'good',
  in_development: 'modified',
  delivered: 'good',
  in_progress: 'modified',
  resolved: 'good',
  declined: 'neutral',
};

export const STATUS_LABEL_KEY: Readonly<Record<FeedbackStatus, string>> = {
  received: 'feedback.status.received',
  needs_info: 'feedback.status.needsInfo',
  ready: 'feedback.status.ready',
  approved_for_development: 'feedback.status.approvedForDevelopment',
  in_development: 'feedback.status.inDevelopment',
  delivered: 'feedback.status.delivered',
  in_progress: 'feedback.status.inProgress',
  resolved: 'feedback.status.resolved',
  declined: 'feedback.status.declined',
};

export const KIND_LABEL_KEY: Readonly<Record<FeedbackKind, string>> = {
  clarification: 'feedback.kind.clarification',
  data: 'feedback.kind.data',
  incident: 'feedback.kind.malfunction',
  enhancement: 'feedback.kind.enhancement',
};

/** The statuses a filter offers, in lifecycle order. `in_progress` is legacy: offered so its rows can be found. */
export const FEEDBACK_STATUS_ORDER: readonly FeedbackStatus[] = REPORT_STATUSES;
export const FEEDBACK_KIND_ORDER: readonly FeedbackKind[] = REPORT_KINDS;

/** Who moved a report, in words. */
export const ACTOR_SIDE_LABEL_KEY: Readonly<Record<ActorSide, string>> = {
  author: 'feedback.actor.author',
  team: 'feedback.actor.team',
  approver: 'feedback.actor.approver',
  platform: 'feedback.actor.platform',
};

/** A status the platform sends that this build does not know is shown as itself (ADR 9058). */
export function statusWords(t: (key: string) => string, status: string): string {
  return Object.hasOwn(STATUS_LABEL_KEY, status) ? t(STATUS_LABEL_KEY[status as FeedbackStatus]) : status;
}

/** A kind the platform sends that this build does not know is shown as itself. */
export function kindWords(t: (key: string) => string, kind: string): string {
  return Object.hasOwn(KIND_LABEL_KEY, kind) ? t(KIND_LABEL_KEY[kind as FeedbackKind]) : kind;
}

/** The mark of a status; an unknown one is neutral, said by its word. */
export function statusMark(status: string): StatusMarkKind {
  return Object.hasOwn(STATUS_MARK, status) ? STATUS_MARK[status as FeedbackStatus] : 'neutral';
}

/**
 * The words for a stored section id. «General» and «New area» are the form's own options; a screen
 * is "chapter · screen" as in the form's picker; an id no screen carries now (a screen renamed or
 * withdrawn since the report) is shown as it was stored rather than guessed at.
 */
export function useSectionLabel(): (id: string) => string {
  const { t } = useTranslation();
  // Every shell screen is named, whoever is looking: a report keeps its section for good.
  const known = useMemo(
    () => new Map(feedbackSections({ administers: true, triages: true }).map((section) => [section.id, section] as const)),
    [],
  );
  return (id) => {
    if (id === GENERAL_SECTION) return t('feedback.section.general');
    if (id === NEW_AREA_SECTION) return t('feedback.section.newArea');
    const section = known.get(id);
    if (section === undefined) return id;
    const screen = section.parentLabelKey === undefined
      ? t(section.labelKey)
      : t('nav.subScreen', { screen: t(section.parentLabelKey), view: t(section.labelKey) });
    return section.chapterLabelKey === section.labelKey
      ? screen
      : t('feedback.section.option', { chapter: t(section.chapterLabelKey), screen });
  };
}
